import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ListingChannelComparisonService } from 'listings/services/listing-channel-comparison.service';
import { ListingSnapshotStatusEnum } from 'settings/interfaces/listing-channel-shanpshot.interface';
import { SELECTED_LANGUAGE_STORAGE_KEY } from 'shared/constants/languages';
import { NotificationStatusBEEnum } from 'shared/constants/notification-statuses';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { Toast } from 'shared/interfaces/toast.interface';
import { pascalToCamelCaseRecursive } from 'shared/util/pascal-to-camel-case';
import { UserNotificationTypeBEEnum } from 'users/enums/user-notification-type.enum';
import { UserNotification, UserNotificationMetadata } from 'users/interfaces/user-notification.interface';
import {
    ListingChannelSnapshotFailureReason,
    ListingChannelSnapshotFailureReasonTranslationMap,
} from './../../listings/enums/listing-channel-snapshot-failure-reason.enum';
import { EventBusService } from './event-bus.service';
import { FeatureFlagService } from './feature-flag.service';
import { ModalService } from './modal.service';
import { RouteHelperService } from './route-helper.service';
import { StorageService } from './storage.service';
import { ToastService } from './toast.service';
import { UserApiService } from './userApi.service';

/**
 * Anything you want to set up as an app-wide event can be configured inside this service.
 * It is injected in app.component, and should only be used there
 */
@Injectable()
export class GlobalEventListenerService {
    private userInformation = toSignal(this.userApiService.getLoginInformation());

    constructor(
        private destroyRef: DestroyRef,
        private eventBusService: EventBusService,
        private routeHelperService: RouteHelperService,
        private toastService: ToastService,
        private translate: TranslateService,
        private router: Router,
        private storageService: StorageService,
        private userApiService: UserApiService,
        private listingChannelComparisonService: ListingChannelComparisonService,
        private modalService: ModalService,
        private featureFlagService: FeatureFlagService,
    ) {}

    public registerGlobalListeners() {
        this.registerLanguageChangeListener();

        if (this.featureFlagService.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON]) {
            this.registerSnapshotStatusUpdateListener();
            this.registerBulkSnapshotNotificationListener();
            this.registerChannelSnapshotErrorNotificationListener();
        }
    }

    private registerLanguageChangeListener() {
        this.eventBusService
            .getObservableForEventStream('changeLanguage')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((newLanguage) => {
                this.translate
                    .use(newLanguage as string)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe();
                this.storageService.setInLocal(SELECTED_LANGUAGE_STORAGE_KEY, newLanguage);
            });
    }

    private registerSnapshotStatusUpdateListener() {
        this.eventBusService
            .getObservableForEventStream('listing:snapshotStatusUpdate')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((statusUpdate: any) => {
                const parsedStatusUpdate = pascalToCamelCaseRecursive(JSON.parse(statusUpdate)) as any;
                // Display logic:
                // Snapshot status updates are sent both for manual snapshot triggers and for each listing that is part of a bulk snapshot.
                // In the case of bulk snapshots, we have a isPartOfBulkOperation property that is set to true, which is false for manually generated snapshots.
                // The toast display conditions are:
                //      - On listings that belong to a bulk snapshot generation, a toast is shown only if the user is actively editing the affected listing
                //      - On listings for which a manual snapshot generation was triggered:
                //          - If actively editing the listing that got a snapshot status update, show the toast regardless of who it is
                //          - If user is anywhere else, a toast will be displayed if the current user is the one that triggered the snapshot generation

                if (parsedStatusUpdate.status === ListingSnapshotStatusEnum.AVAILABLE) {
                    const isOnListingEditPage = this.routeHelperService.isOnListingEditPage(parsedStatusUpdate.listingId);
                    const hasActiveSnapshotData = !!this.listingChannelComparisonService.activeSnapshotPaths();
                    const currentUserId = this.userInformation()?.id;
                    const isCurrentUserOwner = parsedStatusUpdate.snapshotOwnerId === currentUserId;

                    if (isOnListingEditPage) {
                        if (hasActiveSnapshotData) {
                            this.listingChannelComparisonService.newlyGeneratedChannelSnapshot.set(parsedStatusUpdate);
                        }

                        this.toastService.showToast({
                            type: 'actionable',
                            body: 'listings.channelSnapshots.toasts.messages.snapshotReadyWhen.editingListing',
                            args: { channelName: parsedStatusUpdate.channelName },
                            severity: 'success',
                            duration: 30000,
                            listType: 'none',
                            clickHandler: () => {
                                this.listingChannelComparisonService.listingId.set(parsedStatusUpdate.listingId);
                                this.listingChannelComparisonService.activeSnapshotId.set(parsedStatusUpdate.snapshotId);
                                this.listingChannelComparisonService.activeChannelSnapshot.set(parsedStatusUpdate);
                                this.listingChannelComparisonService.newlyGeneratedChannelSnapshot.set(null);
                                this.modalService.closeAllModals();
                            },
                            actionButtonText: 'listings.channelSnapshots.toasts.actions.open',
                            allowDuplicates: true,
                        });
                    } else if (!parsedStatusUpdate.isPartOfBulkOperation && isCurrentUserOwner) {
                        this.toastService.showToast({
                            type: 'actionable',
                            body: 'listings.channelSnapshots.toasts.messages.snapshotReadyWhen.awayFromListing.mainBody',
                            severity: 'success',
                            duration: 30000,
                            listType: 'none',
                            clickHandler: () => {
                                this.router.navigate(['listings/details', parsedStatusUpdate.listingId, 'listingProfile']).then(() => {
                                    this.listingChannelComparisonService.listingId.set(parsedStatusUpdate.listingId);
                                    this.listingChannelComparisonService.activeSnapshotId.set(parsedStatusUpdate.snapshotId);
                                    this.listingChannelComparisonService.activeChannelSnapshot.set(parsedStatusUpdate);
                                    this.modalService.closeAllModals();
                                });
                            },
                            listArguments: [
                                this.translate.instant(
                                    'listings.channelSnapshots.toasts.messages.snapshotReadyWhen.awayFromListing.listingArgument',
                                    { listingName: parsedStatusUpdate.listingName },
                                ),
                                this.translate.instant(
                                    'listings.channelSnapshots.toasts.messages.snapshotReadyWhen.awayFromListing.channelArgument',
                                    { channelName: parsedStatusUpdate.channelName },
                                ),
                            ],
                            actionButtonText: 'listings.channelSnapshots.toasts.actions.viewListing',
                            allowDuplicates: true,
                        });
                    }
                }
            });
    }

    private registerBulkSnapshotNotificationListener() {
        this.eventBusService
            .getObservableForEventStream('notification')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((notificationEvent: any) => {
                const notification = pascalToCamelCaseRecursive(notificationEvent) as UserNotification;

                if (
                    !notification ||
                    Number(notification.notificationType) !== UserNotificationTypeBEEnum.BULK_SNAPSHOT_GENERATION ||
                    Number(notification.status) !== NotificationStatusBEEnum.IN_PROGRESS
                ) {
                    return;
                }

                this.showBulkSnapshotOperationToast(notification);
            });
    }

    private registerChannelSnapshotErrorNotificationListener() {
        this.eventBusService
            .getObservableForEventStream('notification')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((notificationEvent: any) => {
                const notification = pascalToCamelCaseRecursive(notificationEvent) as UserNotification;
                if (
                    !notification ||
                    Number(notification.notificationType) !== UserNotificationTypeBEEnum.SNAPSHOT_GENERATION ||
                    Number(notification.status) !== NotificationStatusBEEnum.ERROR
                ) {
                    return;
                }

                // This only handles channel preview snapshot generation errors
                // The snapshot success logic is registred in registerSnapshotStatusUpdateListener()
                // The registerSnapshotStatusUpdateListener() listens for status updates but due to the backend flow
                // a failure for generating a snapshot does not represent a status update, as the snapshot is only created if the flow succeeded, not failed

                const failureReasonValue = Number(
                    notification.userNotificationMetadataItems.find((meta: UserNotificationMetadata) => meta.key === 'failureReason')
                        ?.value,
                );

                const listingName =
                    notification.userNotificationMetadataItems.find((meta: UserNotificationMetadata) => meta.key === 'listingName')
                        ?.value ?? '';

                const failureReason: ListingChannelSnapshotFailureReason =
                    Number.isInteger(failureReasonValue) && ListingChannelSnapshotFailureReason[failureReasonValue] !== undefined
                        ? failureReasonValue
                        : ListingChannelSnapshotFailureReason.GENERIC_ERROR;

                const failureReasonTranslation = ListingChannelSnapshotFailureReasonTranslationMap[failureReason];

                this.toastService.showToast(failureReasonTranslation, 'error', { listingName: listingName });
            });
    }

    private showBulkSnapshotOperationToast(notification: UserNotification) {
        // For Bulk Snapshot Generation toats, we patch into the user notification that is sent when the bulk operation finishes.
        // The notification is sent to a specific list of users defined by the workers, so no additional checks are needed like the toasts that are displayed on a per-listing basis.

        const totalCount = Number(
            notification.userNotificationMetadataItems.find((meta: UserNotificationMetadata) => meta.key === 'totalListings')?.value,
        );
        const successfulCount = Number(
            notification.userNotificationMetadataItems.find((meta: UserNotificationMetadata) => meta.key === 'successfulListings')?.value,
        );
        const failedCount = totalCount - successfulCount;

        let toast: Toast;
        if (failedCount === totalCount) {
            toast = {
                body: 'listings.channelSnapshots.toasts.messages.bulkChannelSnapshot.failed.body',
                listArguments: [
                    this.translate.instant(
                        'listings.channelSnapshots.toasts.messages.bulkChannelSnapshot.failed.listingsProcessedArgument',
                        { count: totalCount },
                    ),
                ],
                severity: 'error',
                listType: 'none',
            };
        } else if (failedCount > 0) {
            toast = {
                body: 'listings.channelSnapshots.toasts.messages.bulkChannelSnapshot.warning.body',
                listArguments: [
                    this.translate.instant(
                        'listings.channelSnapshots.toasts.messages.bulkChannelSnapshot.warning.listingsProcessedArgument',
                        { count: totalCount },
                    ),
                    this.translate.instant(
                        'listings.channelSnapshots.toasts.messages.bulkChannelSnapshot.warning.listingsSuccessfulArgument',
                        { count: successfulCount },
                    ),
                    this.translate.instant('listings.channelSnapshots.toasts.messages.bulkChannelSnapshot.warning.listingsFailedArgument', {
                        count: failedCount,
                    }),
                ],
                severity: 'warning',
                listType: 'unordered',
            };
        } else {
            toast = {
                body: 'listings.channelSnapshots.toasts.messages.bulkChannelSnapshot.successful.body',
                severity: 'success',
            };
        }

        this.toastService.showToast({
            ...toast,
            duration: 5000,
        });
    }
}
